import React, { useState } from 'react';
import { Download, Send, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const BACKEND_URL = import.meta.env.VITE_API_URL || '';

interface Props {
  incident: any;
  userRole: string;
  onAction?: () => void;
}

export const IncidentActions: React.FC<Props> = ({ incident, userRole, onAction }) => {
  const [loading, setLoading] = useState('');
  const [msg, setMsg]         = useState('');

  const downloadPDF = async () => {
    setLoading('pdf');
    try {
      const res = await fetch(`${BACKEND_URL}/api/incidents/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(incident),
      });
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `incident_${incident.incident_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { setMsg('Erreur génération PDF'); }
    finally { setLoading(''); }
  };

  const escalate = async (action: string, notes = '') => {
    setLoading(action);
    try {
      const res  = await fetch(`${BACKEND_URL}/api/incidents/escalate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ incident_id: incident.incident_id, action, notes, actor: incident.reported_by, role: userRole }),
      });
      const data = await res.json();
      if (data.success) { setMsg(`Action "${action}" enregistrée`); onAction?.(); }
    } catch { setMsg('Erreur serveur'); }
    finally { setLoading(''); }
  };

  return (
    <div className="space-y-3">
      {msg && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-2">
          <p className="text-green-400 text-xs">{msg}</p>
        </div>
      )}

      {/* PDF — all roles */}
      <button onClick={downloadPDF} disabled={loading === 'pdf'}
        className="w-full flex items-center gap-2 px-4 py-2 bg-navy-800 hover:bg-navy-700 border border-navy-600 rounded-lg text-gray-300 text-xs transition-colors disabled:opacity-50">
        <Download className="w-3.5 h-3.5"/>
        {loading === 'pdf' ? 'Génération...' : 'Télécharger rapport PDF'}
      </button>

      {/* Commissaire actions */}
      {(userRole === 'COMMISSAIRE' || userRole === 'SUPER_ADMIN') && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Actions Commissaire</p>
          <button onClick={() => escalate('acknowledge')} disabled={!!loading}
            className="w-full flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-lg text-orange-400 text-xs hover:bg-orange-500/20 transition-colors disabled:opacity-50">
            <Send className="w-3.5 h-3.5"/> Prendre en charge
          </button>
          {(incident.severity === '1' || incident.severity === '2') && (
            <button onClick={() => escalate('escalate_dg', 'Incident critique — escalade DG requise')} disabled={!!loading}
              className="w-full flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs hover:bg-red-500/20 transition-colors disabled:opacity-50">
              <AlertTriangle className="w-3.5 h-3.5"/> Escalader au DG
            </button>
          )}
          <button onClick={() => escalate('resolve')} disabled={!!loading}
            className="w-full flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-xs hover:bg-green-500/20 transition-colors disabled:opacity-50">
            <CheckCircle className="w-3.5 h-3.5"/> Marquer résolu
          </button>
        </div>
      )}

      {/* DG actions */}
      {(userRole === 'DG' || userRole === 'SUPER_ADMIN') && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Décision DG</p>
          <button onClick={() => escalate('approve', 'Intervention approuvée par le DG')} disabled={!!loading}
            className="w-full flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-xs hover:bg-green-500/20 transition-colors disabled:opacity-50">
            <CheckCircle className="w-3.5 h-3.5"/> Approuver intervention
          </button>
          <button onClick={() => escalate('reject', 'Reclassification requise')} disabled={!!loading}
            className="w-full flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs hover:bg-red-500/20 transition-colors disabled:opacity-50">
            <XCircle className="w-3.5 h-3.5"/> Rejeter / Reclassifier
          </button>
          <button onClick={() => escalate('close')} disabled={!!loading}
            className="w-full flex items-center gap-2 px-4 py-2 bg-gray-500/10 border border-gray-500/30 rounded-lg text-gray-400 text-xs hover:bg-gray-500/20 transition-colors disabled:opacity-50">
            Clôturer le dossier
          </button>
        </div>
      )}
    </div>
  );
};
